<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import {
    CloseBanner,
    InfoCircle,
    Time,
    CheckCircle,
    AlertTriangleIcon,
    AlertCircleAltIcon,
  } from '../icons';

  export let title: string = '',
    message: string = '',
    isClosable: boolean = true,
    mobileFixed: boolean = false,
    variant:
      | 'primary'
      | 'destructive'
      | 'crimson'
      | 'grey'
      | 'success'
      | 'alert' = 'primary',
    sticky = false,
    linkText: string = '',
    action: () => void = () => {};

  const dispatch = createEventDispatcher();
  const mapIcon = {
    primary: InfoCircle,
    destructive: AlertTriangleIcon,
    crimson: AlertCircleAltIcon,
    grey: Time,
    success: CheckCircle,
    alert: InfoCircle,
  };
</script>

<div class="alertBanner alertBanner-{variant}" class:mobileFixed class:sticky>
  {#if isClosable}
    <button class="close-button" on:click={() => dispatch('close')}>
      <CloseBanner />
    </button>
  {/if}
  <div class="alertBanner-wrapper">
    <div class="icon">
      <svelte:component this={mapIcon[variant]} />
    </div>
    <div class="alertBanner-content">
      {#if title}
        <h5 class="title">{title}</h5>
      {/if}
      <div class="alertBanner-action">
        {#if message}
          <p class="text-small">{message}</p>
        {/if}
        {#if linkText}
          <button on:click={action}
            >{linkText}
            <svg
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.25011 14.7558L14.0059 9.99997L9.25011 5.24414L8.07178 6.42247L11.6493 9.99997L8.07178 13.5775L9.25011 14.7558Z"
                fill="#058BEB"
              />
            </svg>
          </button>
        {/if}
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .alertBanner {
    position: relative;
    display: flex;
    width: 100%;
    flex-direction: column;
    border-radius: 8px;
    grid-gap: 8px;
    padding: 16px;
    .close-button {
      position: absolute;
      top: 5px;
      right: 5px;
      background: none;
      border: none;
      cursor: pointer;
    }
    .alertBanner-wrapper {
      display: flex;
      gap: var(--spacing-micro, 12px);
    }
    @media screen and (max-width: 989px) {
      &.mobileFixed {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        margin: 0 !important;
        border-radius: 0 !important;
        z-index: 9999;
      }
    }

    h5 {
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px;
      letter-spacing: 0.16px;
    }

    .icon {
      display: flex;
      align-items: flex-start;
      gap: 8px;

      > :global(svg) {
        height: 20px;
        width: 20px;
      }
    }

    .alertBanner-content {
      display: flex;
      flex-direction: column;
      width: 100%;

      .title {
        margin-bottom: var(--spacing-nano);
      }

      .alertBanner-action {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-micro, 12px);
        @media screen and (max-width: 767px) {
          align-items: flex-start;
          flex-direction: column;
        }
      }

      button {
        color: var(--color-primary);
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
        letter-spacing: 0.16px;
        display: flex;
        align-items: center;
        text-decoration: none;
        text-wrap: nowrap;
        background: unset;
        border: unset;
        cursor: pointer;
      }
    }

    &-primary {
      border: 1px solid var(--color-primary);
      background: var(--color-primary-light);
      h5,
      p {
        color: var(--color-primary);
      }
      .icon {
        color: var(--color-primary);
      }
    }

    &-destructive {
      border: 1px solid var(--color-error);
      background: var(--color-error-light);
      h5,
      p {
        color: var(--color-error);
      }
      .icon {
        color: var(--color-error);
      }
    }

    &-crimson {
      border: 1px solid var(--color-error);
      background: #fee4e2;

      h5,
      p,
      .icon {
        color: var(--color-error);
      }
    }

    &-grey {
      border: 1px solid var(--color-neutralBlack);
      background: var(--color-background);
      h5,
      p {
        color: var(--color-neutralBlack);
      }
      .icon {
        color: var(--color-neutralBlack);
      }
    }
    &-success {
      border: 1px solid var(--color-success);
      background: var(--color-success-light);
      h5,
      p {
        color: var(--color-success);
      }
      .icon {
        color: var(--color-success);
      }
    }
    &-alert {
      border: 1px solid var(--color-alert);
      background: var(--color-alert-light);
      h5,
      p {
        color: var(--color-alert);
      }
      .icon {
        color: var(--color-alert);
      }
    }

    &.sticky {
      padding: 6px;
      border-radius: 0;
      border: none;

      @media screen and (max-width: 767px) {
        padding: var(--spacing-nano) var(--spacing-mini);
      }

      .alertBanner {
        &-wrapper {
          justify-content: center;
        }

        &-content {
          width: fit-content;
        }

        &-action {
          @media screen and (max-width: 767px) {
            flex-direction: row;
            align-items: center;
          }
        }
      }

      .icon {
        align-items: center;

        @media screen and (max-width: 375px) {
          display: none;
        }
      }

      button {
        flex-shrink: 0;
        font-size: 14px;
      }
    }
  }
</style>
